import { $enum } from "ts-enum-util";
import { type InferType, mixed, number, object, string } from "yup";

import { MyLensesLensCtaText, MyLensesLensCtaType } from "../../gql-generated/types";
import { messages } from "../components/LensCtaForm/messages.generated";

import { messages as errorMessages } from "./messages.generated";

// TODO(i18n): Only handles English traslation
// need to update solution to allow i18n
const {
    formLabelText: { defaultMessage: labelText },
    formLabelType: { defaultMessage: labelType },
    formLabelUrl: { defaultMessage: labelUrl },
    formLabelUri: { defaultMessage: labelUri },
    formLabelAndroidAppUrl: { defaultMessage: labelAndroidAppUrl },
    formLabelIosAppId: { defaultMessage: labelIosAppId },
    formLabelAppName: { defaultMessage: labelAppName },
} = messages;

const {
    errorUriPattern: { defaultMessage: errorUriPattern },
    errorUrlType: { defaultMessage: errorUrlType },
    errorIosAppId: { defaultMessage: errorIosAppId },
} = errorMessages;

const deeplinkStarterSchemas = {
    androidAppUrl: string().label(labelAndroidAppUrl),
    appName: string().label(labelAppName),
    iosAppId: number().label(labelIosAppId),
    uri: string().label(labelUri),
};

const deeplinkValidationSchemas = {
    androidAppUrl: deeplinkStarterSchemas.androidAppUrl.required(),
    appName: deeplinkStarterSchemas.appName.required(),
    iosAppId: deeplinkStarterSchemas.iosAppId.integer(errorIosAppId).required(),
    // https://stackoverflow.com/questions/1547899/which-characters-make-a-url-invalid
    uri: deeplinkStarterSchemas.uri
        .matches(/^[\w!#$&'()*+,./:;=?@[\]~-]*$/gm, errorUriPattern) // check unescaped chars
        .test("is-valid-uri", errorUriPattern, (value) => {
            if (!value) {
                return false;
            }
            try {
                new URL(value);
            } catch {
                return false;
            }
            return true;
        })
        .required(),
};

const deeplinkSchema = object({ ...deeplinkStarterSchemas }).when("type", {
    is: MyLensesLensCtaType.CtaTypeDeeplink,
    // eslint-disable-next-line unicorn/no-thenable -- yup interface includes then
    then: (schema) =>
        schema
            // eslint-disable-next-line unicorn/prefer-spread -- yup interface
            .concat(
                object({
                    ...deeplinkValidationSchemas,
                }),
            )
            .required(),
    otherwise: (schema) => schema.nullable().optional(),
});

export type DeeplinkSchema = InferType<typeof deeplinkSchema>;

const websiteStarterSchemas = {
    url: string().label(labelUrl),
};

const websiteValidationSchemas = {
    url: websiteStarterSchemas.url.url(errorUrlType).required(),
};

const websiteSchema = object({ ...websiteStarterSchemas }).when("type", {
    is: MyLensesLensCtaType.CtaTypeWebsite,
    // eslint-disable-next-line unicorn/no-thenable -- yup interface includes then
    then: (schema) =>
        schema
            // eslint-disable-next-line unicorn/prefer-spread -- yup interface
            .concat(
                object({
                    ...websiteValidationSchemas,
                }),
            )
            .required(),
    otherwise: (schema) => schema.nullable().optional(),
});

export type WebsiteSchema = InferType<typeof websiteSchema>;

export const EditLensCtaFormSchema = object({
    text: mixed<MyLensesLensCtaText>().oneOf($enum(MyLensesLensCtaText).getValues()).label(labelText).required(),
    type: mixed<MyLensesLensCtaType>().oneOf($enum(MyLensesLensCtaType).getValues()).label(labelType).required(),
    deeplink: deeplinkSchema,
    website: websiteSchema,
});

export type EditLensCtaFormValues = InferType<typeof EditLensCtaFormSchema>;
